import { Box, Grid, Stack, Typography } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import React, { useRef, useState } from 'react';
import Images from '../../../utils/Images';
import EnquiryForm from './EnquiryForm';
import CoffeeSection from './CoffeeSection';
import AdvantageSection from './AdvantageSection';
import FranchiseFormat from './FranchiseFormat';
import { Helmet } from 'react-helmet-async';

const FranchisePage = () => {
    const targetRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: targetRef,
        offset: ['start start', 'end start']
    });

    const visionRef = useRef(null);
    const firstOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
    const firstY = useTransform(scrollYProgress, [0, 0.3], [0, -100]);
    const secondY = useTransform(scrollYProgress, [0.3, 0.6], [-100, 0]);

    const floatingTransition = {
        duration: 8,
        repeat: Infinity,
        repeatType: 'mirror',
        ease: 'easeInOut',
    };

    const cloudFloatVariants = {
        cafeCloud: { y: [0, -20, 0], x: [0, 10, 0], transition: { ...floatingTransition, duration: 8 } },
        float1: { y: [0, -20, 0], x: [0, 10, 0], transition: { ...floatingTransition, duration: 12 } },
        float2: { y: [0, 15, 0], x: [0, -15, 0], transition: { ...floatingTransition, duration: 10 } },
        float3: { y: [0, -10, 10, 0], transition: { ...floatingTransition, duration: 14 } },
        float4: { y: [0, 20, 0], x: [0, 5, 0], transition: { ...floatingTransition, duration: 11 } },
        float5: { y: [0, -15, 5, 0], transition: { ...floatingTransition, duration: 13 } },
        float6: { y: [0, -15, 5, 0], transition: { ...floatingTransition, duration: 13 } },
        float7: { y: [0, -15, 5, 0], transition: { ...floatingTransition, duration: 13 } },
        float8: { y: [0, -15, 5, 0], transition: { ...floatingTransition, duration: 13 } },
        float9: { y: [0, -15, 5, 0], transition: { ...floatingTransition, duration: 13 } },
        float10: { y: [0, -15, 5, 0], transition: { ...floatingTransition, duration: 13 } },
    };

    return (
        <>
            <Helmet>
                <title>Franchise with Xpanse – Start Your Own Tech-Driven Coffee Cafe</title>
                <meta name="description" content="Partner with Xpanse Cafe and launch your own tech-driven franchise. Zero franchise fee. Zero royalties until profit. Join the new wave of digital-first cafes." />
                <meta property="og:title" content="Franchise with Xpanse" />
                <meta property="og:description" content="Join the coffee revolution. No franchise fees. No royalties until profit." />
                <meta property="og:url" content="https://xpansecafe.com/franchise" />
                <meta property="og:type" content="website" />
                <link rel="canonical" href="https://xpansecafe.com/franchise" />
            </Helmet>
            <Box className="franchisePage" sx={{ position: 'relative', overflow: 'hidden' }}>

                <section className='introSection' ref={targetRef} style={{ height: '200vh', position: 'relative' }}>
                    <motion.div
                        style={{
                            position: 'sticky',
                            top: 0,
                            height: '100vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: firstOpacity,
                            y: firstY,
                            zIndex: 2
                        }}
                    >
                        <Grid container justifyContent={'center'} alignItems={'center'} padding={6} display="flex" flexDirection={{ xs: 'column-reverse', md: 'row' }}>
                            <motion.img
                                src={Images.cloud1}
                                className="cloud top-cloud"
                                variants={cloudFloatVariants}
                                animate="float1"
                            />

                            <motion.img
                                src={Images.cloud2}
                                className="cloud top-right"
                                variants={cloudFloatVariants}
                                animate="float2"
                            />

                            <motion.img
                                src={Images.cloud3}
                                className="cloud bottom-left"
                                variants={cloudFloatVariants}
                                animate="float3"
                            />

                            <motion.img
                                src={Images.cloud4}
                                className="cloud bottom-center"
                                variants={cloudFloatVariants}
                                animate="float4"
                            />

                            <motion.img
                                src={Images.cloud5}
                                className="cloud bottom-right"
                                variants={cloudFloatVariants}
                                animate="float5"
                            />
                            <Grid item xs={12} sm={5} md={5}>
                                <Typography className='magicText'>Your Cafe, Our <i>Magic</i>.</Typography>
                                <Typography className='buildText'>Let's build.</Typography>
                                <Typography className='inventText'>
                                    We're tech-first coffee brand  - now inviting<br /> franchises to scale with us
                                </Typography>
                                <Stack direction={'row'} alignItems={'center'} marginTop={3} marginBottom={2}>
                                    <img className="bulletPointImage" alt={Images.bulletPoint} src={Images.bulletPoint} />
                                    <Typography className='bulletPoint' marginLeft={2}>Zero Franchise Fee</Typography>
                                </Stack>
                                <Stack direction={'row'} alignItems={'center'}>
                                    <img className="bulletPointImage" alt={Images.bulletPoint} src={Images.bulletPoint} />
                                    <Typography className='bulletPoint' marginLeft={2}>Zero Royalties Until You Profit</Typography>
                                </Stack>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                    width: { xs: '40%', sm: '40%', md: '100%' },
                                    display: 'flex',
                                    justifyContent: { xs: 'center', sm: 'center', md: 'flex-start' },
                                    alignItems: 'center'
                                }}
                            >
                                <motion.img
                                    alt={Images.cafeCloud}
                                    src={Images.cafeCloud}
                                    variants={cloudFloatVariants}
                                    animate="cafeCloud"
                                    className="cafeCloud"
                                />
                            </Grid>

                        </Grid>
                    </motion.div>

                    <div ref={visionRef} style={{ height: '150vh', position: 'relative' }}>
                        <motion.div
                            style={{
                                position: 'sticky',
                                top: 0,
                                minHeight: '100vh',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                // opacity: secondOpacity,
                                y: secondY,
                                zIndex: 2,
                            }}
                        >
                            <motion.img
                                src={Images.cloud1}
                                className="cloud vision-top-cloud"
                                variants={cloudFloatVariants}
                                animate="float6"
                            />
                            <motion.img
                                src={Images.cloud2}
                                className="cloud vision-top-right"
                                variants={cloudFloatVariants}
                                animate="float7"
                            />
                            <motion.img
                                src={Images.cloud3}
                                className="cloud vision-bottom-left"
                                variants={cloudFloatVariants}
                                animate="float8"
                            />
                            <motion.img
                                src={Images.cloud4}
                                className="cloud vision-bottom-center"
                                variants={cloudFloatVariants}
                                animate="float9"
                            />
                            <motion.img
                                src={Images.cloud5}
                                className="cloud vision-bottom-right"
                                variants={cloudFloatVariants}
                                animate="float10"
                            />
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                <img style={{ width: '100%' }} src={Images.vision} alt="vision-desktop" />
                            </Box>

                            <Box sx={{ display: { xs: 'block', md: 'none' }, padding: { xs: 3, sm: 2 } }} >
                                <img style={{ width: '100%' }} src={Images.visionMob} alt="vision-mobile" />
                            </Box>

                        </motion.div>
                    </div>
                </section>

                <CoffeeSection />

                <AdvantageSection />

                <FranchiseFormat />

                <EnquiryForm />

            </Box>
        </>
    );
};

export default FranchisePage;
